<template>
  <div class="mtp-dd">
    <button
      type="button"
      class="mtp-trigger"
      :class="{ 'mtp-trigger--open': open }"
      :disabled="loading"
      @click.stop="toggle()"
    >
      <span
        class="mtp-trigger__label"
        :class="{ 'mtp-trigger__label--placeholder': !value }"
      >
        <v-icon v-if="value" size="14" color="var(--app-accent)" class="mr-1">mdi-cube-outline</v-icon>
        {{ selectedLabel }}
      </span>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="14"
        width="2"
        color="var(--app-accent)"
      />
      <svg
        v-else
        class="mtp-trigger__chev"
        :class="{ 'mtp-trigger__chev--open': open }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd" />
      </svg>
    </button>
    <div v-if="open" class="mtp-panel" @click.stop>
      <div class="mtp-search">
        <svg
          class="mtp-search__icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/>
        </svg>
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          :placeholder="searchPlaceholder"
          class="mtp-search__input"
          @click.stop
        />
      </div>
      <div
        class="mtp-option"
        :class="{ 'mtp-option--selected': !value }"
        @click="pick(null)"
      >
        <svg
          v-if="!value"
          class="mtp-option__check"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clip-rule="evenodd"/>
        </svg>
        <span class="mtp-option__none">{{ noneLabel }}</span>
      </div>
      <div
        v-for="m in filtered"
        :key="m.uuid"
        class="mtp-option"
        :class="{ 'mtp-option--selected': value === m.uuid }"
        @click="pick(m.uuid)"
      >
        <svg
          v-if="value === m.uuid"
          class="mtp-option__check"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clip-rule="evenodd"/>
        </svg>
        <div class="mtp-option__text">
          <span class="mtp-option__name">{{ m.appName }}</span>
          <span class="mtp-option__key" v-if="m.serviceKey">{{ m.serviceKey }}</span>
        </div>
      </div>
      <div v-if="!filtered.length" class="mtp-empty">
        No modules match "{{ query }}"
      </div>
    </div>
  </div>
</template>

<script>
/**
 * HRDC-themed module picker dropdown (replaces the native <select>). Shared
 * by the PDF Config and Email Template tabs so the "copy from existing module"
 * control looks and behaves identically in both places.
 *
 * Controlled via :value (the selected module uuid, or null) and emits a
 * `select` event with the chosen uuid (null = the "none" option). The parent
 * owns what happens on selection (e.g. load PDF fields, copy email HTML).
 */
export default {
  name: "ModuleTemplatePicker",
  props: {
    value: { type: String, default: null },
    modules: { type: Array, default: () => [] },
    placeholder: { type: String, default: "Select a module to copy from…" },
    searchPlaceholder: { type: String, default: "Search modules..." },
    noneLabel: { type: String, default: "— None —" },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      query: "",
    };
  },
  computed: {
    selectedLabel() {
      if (!this.value) return this.placeholder;
      const m = this.modules.find((x) => x.uuid === this.value);
      return m ? m.appName : this.placeholder;
    },
    filtered() {
      const q = (this.query || "").trim().toLowerCase();
      if (!q) return this.modules;
      return this.modules.filter(
        (m) =>
          (m.appName || "").toLowerCase().includes(q) ||
          (m.serviceKey || "").toLowerCase().includes(q)
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
  methods: {
    toggle() {
      if (this.loading) return;
      this.open = !this.open;
      this.query = "";
      if (this.open) {
        this.$nextTick(() => {
          const el = this.$refs.searchInput;
          if (el && el.focus) el.focus();
        });
      }
    },
    pick(uuid) {
      this.open = false;
      this.query = "";
      this.$emit("select", uuid);
    },
    handleDocumentClick() {
      // Outside clicks (inside-panel clicks call .stop) dismiss the dropdown.
      if (this.open) this.open = false;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .mtp-trigger {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
  color: var(--app-text);
}

.dark-mode .mtp-trigger:hover:not(:disabled) {
  border-color: var(--app-border-strong);
}

.dark-mode .mtp-trigger:disabled {
  background: var(--app-elevated);
}

.dark-mode .mtp-trigger__label--placeholder {
  color: var(--app-text-faint);
}

.dark-mode .mtp-trigger__chev {
  color: var(--app-text-faint);
}

.dark-mode .mtp-panel {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .mtp-search {
  background: var(--app-card);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .mtp-search__icon {
  color: var(--app-text-faint);
}

.dark-mode .mtp-search__input {
  color: var(--app-text);
}

.dark-mode .mtp-search__input::placeholder {
  color: var(--app-text-faint);
}

.dark-mode .mtp-option:hover {
  background: var(--app-elevated);
}

.dark-mode .mtp-option--selected {
  background: var(--app-accent-soft);
}

.dark-mode .mtp-option__name {
  color: var(--app-text);
}

.dark-mode .mtp-option__key {
  color: var(--app-text-faint);
}

.dark-mode .mtp-option__none {
  color: var(--app-text-muted);
}

.dark-mode .mtp-empty {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

.mtp-dd { position: relative; font-family: 'Lato', 'Segoe UI', sans-serif; }
.mtp-trigger {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.84rem;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-height: 40px;
}
.mtp-trigger:hover:not(:disabled) { border-color: #94a3b8; }
.mtp-trigger--open,
.mtp-trigger:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.mtp-trigger:disabled { background: #f1f5f9; cursor: not-allowed; }
.mtp-trigger__label {
  flex: 1; min-width: 0;
  display: flex; align-items: center;
  font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mtp-trigger__label--placeholder { color: #94a3b8; font-weight: 500; }
.mtp-trigger__chev {
  width: 16px; height: 16px;
  color: #94a3b8;
  flex-shrink: 0; margin-left: 8px;
  transition: transform 0.15s;
}
.mtp-trigger__chev--open { transform: rotate(180deg); }

.mtp-panel {
  position: absolute;
  top: calc(100% + 6px); left: 0; right: 0;
  z-index: 30;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 30px -8px rgba(15, 23, 42, 0.2),
              0 4px 10px -4px rgba(15, 23, 42, 0.08);
  max-height: 260px;
  overflow-y: auto;
  padding: 5px;
  animation: mtp-panel-in 0.12s ease-out;
}
@keyframes mtp-panel-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.mtp-search {
  position: sticky; top: -5px;
  z-index: 1;
  display: flex; align-items: center; gap: 6px;
  padding: 7px 8px;
  margin: -5px -5px 5px;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
}
.mtp-search__icon { width: 15px; height: 15px; color: #94a3b8; flex-shrink: 0; }
.mtp-search__input {
  flex: 1; min-width: 0;
  border: none; outline: none; background: transparent;
  font-size: 0.8rem; color: #0f172a; padding: 2px 0;
}
.mtp-search__input::placeholder { color: #94a3b8; }
.mtp-option {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.1s;
}
.mtp-option:hover { background: #f1f5f9; }
.mtp-option--selected { background: #e0f2fe; }
.mtp-option--selected:hover { background: #bae6fd; }
.mtp-option__check {
  width: 15px; height: 15px; color: #0ea5e9; flex-shrink: 0;
}
.mtp-option:not(.mtp-option--selected) .mtp-option__text,
.mtp-option:not(.mtp-option--selected) .mtp-option__none {
  margin-left: 23px; /* align with options that show a check */
}
.mtp-option__text { display: flex; flex-direction: column; min-width: 0; }
.mtp-option__name {
  font-size: 0.82rem; font-weight: 600; color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mtp-option--selected .mtp-option__name { color: #0c4a6e; }
.mtp-option__key {
  font-size: 0.68rem; color: #94a3b8;
  font-family: 'Menlo', 'Monaco', monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mtp-option__none {
  font-size: 0.82rem; font-weight: 600; color: #64748b; font-style: italic;
}
.mtp-empty {
  padding: 12px 8px; text-align: center;
  font-size: 0.76rem; color: #94a3b8; font-style: italic;
}
</style>
